/**
 * Help Center Utilities
 * Manages help article loading and metadata
 */

export interface HelpArticle {
  slug: string;
  title: string;
  description: string;
  category: 'getting-started' | 'configuration' | 'features' | 'troubleshooting';
  content: string;
  readTime: number; // minutes
}

export const helpArticles: Omit<HelpArticle, 'content'>[] = [
  // Getting Started
  {
    slug: 'getting-started',
    title: 'Getting Started with Stock Sentry',
    description: 'Install Stock Sentry and configure your first inventory alerts in minutes',
    category: 'getting-started',
    readTime: 5,
  },
  {
    slug: 'quick-setup',
    title: 'Quick Setup Guide',
    description: 'Set up Stock Sentry in 3 easy steps and start monitoring your inventory',
    category: 'getting-started',
    readTime: 3,
  },
  {
    slug: 'dashboard-overview',
    title: 'Dashboard Overview',
    description: "Navigate Stock Sentry's dashboard and understand your inventory status at a glance",
    category: 'getting-started',
    readTime: 4,
  },

  // Configuration
  {
    slug: 'alert-thresholds',
    title: 'Configuring Alert Thresholds',
    description: 'Set custom warning and urgent thresholds for proactive stockout prevention',
    category: 'configuration',
    readTime: 7,
  },
  {
    slug: 'notifications',
    title: 'Notification Preferences',
    description: 'Choose email, Slack, or digest notifications to match your workflow',
    category: 'configuration',
    readTime: 6,
  },
  {
    slug: 'seasonal-adjustments',
    title: 'Seasonal Pattern Recognition',
    description: 'Enable automatic seasonal adjustments for more accurate predictions',
    category: 'configuration',
    readTime: 5,
  },

  // Features
  {
    slug: 'velocity-calculations',
    title: 'Sales Velocity Explained',
    description: 'Understand how Stock Sentry calculates sales velocity and predicts stockouts',
    category: 'features',
    readTime: 8,
  },
  {
    slug: 'smart-alerts',
    title: 'Smart Alert System',
    description: 'Learn how Stock Sentry prioritizes alerts and reduces notification fatigue',
    category: 'features',
    readTime: 6,
  },
  {
    slug: 'inventory-tracking',
    title: 'Real-Time Inventory Tracking',
    description: 'Automatic inventory sync with your Shopify store for up-to-date insights',
    category: 'features',
    readTime: 4,
  },
  {
    slug: 'cloudflare-infrastructure',
    title: 'Cloudflare Infrastructure Guide',
    description: 'Learn how Stock Sentry leverages Cloudflare for performance and reliability',
    category: 'features',
    readTime: 12,
  },

  // Troubleshooting
  {
    slug: 'faq',
    title: 'Frequently Asked Questions',
    description: 'Pricing, privacy, security, and common questions answered',
    category: 'troubleshooting',
    readTime: 10,
  },
  {
    slug: 'missing-alerts',
    title: 'Not Receiving Alerts',
    description: "Troubleshoot why you're not receiving inventory alerts",
    category: 'troubleshooting',
    readTime: 5,
  },
  {
    slug: 'incorrect-predictions',
    title: 'Inaccurate Stockout Predictions',
    description: 'Fix issues with velocity calculations and improve prediction accuracy',
    category: 'troubleshooting',
    readTime: 7,
  },
];

export function getArticleBySlug(slug: string): Omit<HelpArticle, 'content'> | undefined {
  return helpArticles.find((article) => article.slug === slug);
}

export function getArticlesByCategory(category: HelpArticle['category']): Omit<HelpArticle, 'content'>[] {
  return helpArticles.filter((article) => article.category === category);
}

export async function loadArticleContent(slug: string): Promise<string> {
  // In production, this would load from the filesystem
  // For now, we'll return a placeholder that the route loader will handle
  return '';
}

export const categoryLabels: Record<HelpArticle['category'], string> = {
  'getting-started': 'Getting Started',
  configuration: 'Configuration',
  features: 'Features',
  troubleshooting: 'Troubleshooting',
};

export const categoryDescriptions: Record<HelpArticle['category'], string> = {
  'getting-started': 'New to Stock Sentry? Start here to learn the basics.',
  configuration: 'Configure Stock Sentry to match your workflow and preferences.',
  features: 'Learn about Stock Sentry\'s features and how to use them effectively.',
  troubleshooting: 'Common issues and how to resolve them.',
};
